import { useState } from 'react';
// import { unifiedApiService } from '../../services/unified';
import { LoadingSpinner } from '../layout/LoadingSpinner';
import type { Profile, ProfilePreferences } from '../../types/profile';

interface PreferencesSectionProps {
  profile: Profile | null;
  onUpdate: (_profile: Profile) => void;
}

export function PreferencesSection({ profile, onUpdate: _onUpdate }: PreferencesSectionProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [preferences, setPreferences] = useState<ProfilePreferences>(
    // TODO: Add preferences to backend Profile model
    // For now using default values, will use profile.preferences when implemented
    profile ? {
      emailNotifications: true,
      smsNotifications: false,
      jobAlerts: true,
      marketingEmails: false,
      profileVisibility: 'recruiters',
      preferredContactMethod: 'email',
    } : {
      emailNotifications: true,
      smsNotifications: false,
      jobAlerts: true,
      marketingEmails: false,
      profileVisibility: 'recruiters',
      preferredContactMethod: 'email',
    }
  );

  const handleToggleChange = (key: keyof ProfilePreferences, value: boolean) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
    setError(null);
    setSuccess(null);
  };

  const handleSelectChange = (key: keyof ProfilePreferences, value: string) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
    setError(null);
    setSuccess(null);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // TODO: Unified API doesn't support preferences yet
      // const updatedProfile = await unifiedApiService.profile.updateProfile({
      //   preferences,
      // });

      // onUpdate(updatedProfile);
      setSuccess('Preferences updated successfully (Note: Backend support pending)');
    } catch (err) {
      const error = err as Error;
      setError(error.message || 'Failed to update preferences');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-lg font-medium text-gray-900 mb-2">Preferences & Privacy</h2>
        <p className="text-sm text-gray-600">
          Manage your notification preferences and privacy settings.
        </p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-800">{success}</p>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-8">
        {/* Notification Preferences */}
        <div>
          <h3 className="text-md font-medium text-gray-900 mb-4">Notification Preferences</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Email Notifications</label>
                <p className="text-sm text-gray-500">Receive important updates via email</p>
              </div>
              <button
                type="button"
                onClick={() => handleToggleChange('emailNotifications', !preferences.emailNotifications)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  preferences.emailNotifications ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    preferences.emailNotifications ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">SMS Notifications</label>
                <p className="text-sm text-gray-500">Receive urgent updates via text message</p>
              </div>
              <button
                type="button"
                onClick={() => handleToggleChange('smsNotifications', !preferences.smsNotifications)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  preferences.smsNotifications ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    preferences.smsNotifications ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Job Alerts</label>
                <p className="text-sm text-gray-500">Get notified about new job opportunities</p>
              </div>
              <button
                type="button"
                onClick={() => handleToggleChange('jobAlerts', !preferences.jobAlerts)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  preferences.jobAlerts ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    preferences.jobAlerts ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Marketing Emails</label>
                <p className="text-sm text-gray-500">Receive newsletters and promotional content</p>
              </div>
              <button
                type="button"
                onClick={() => handleToggleChange('marketingEmails', !preferences.marketingEmails)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  preferences.marketingEmails ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    preferences.marketingEmails ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Privacy Settings */}
        <div>
          <h3 className="text-md font-medium text-gray-900 mb-4">Privacy Settings</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Profile Visibility
              </label>
              <select
                value={preferences.profileVisibility}
                onChange={(e) => handleSelectChange('profileVisibility', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="public">Public - Visible to everyone</option>
                <option value="recruiters">Recruiters Only - Visible to verified recruiters</option>
                <option value="private">Private - Only visible to you</option>
              </select>
              <p className="mt-1 text-sm text-gray-500">
                Control who can view your profile information
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preferred Contact Method
              </label>
              <select
                value={preferences.preferredContactMethod}
                onChange={(e) => handleSelectChange('preferredContactMethod', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="email">Email</option>
                <option value="phone">Phone</option>
                <option value="both">Both Email and Phone</option>
              </select>
              <p className="mt-1 text-sm text-gray-500">
                How would you prefer recruiters to contact you?
              </p>
            </div>
          </div>
        </div>

        {/* Data & Privacy */}
        <div>
          <h3 className="text-md font-medium text-gray-900 mb-4">Data & Privacy</h3>
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Data Export</h4>
              <p className="text-sm text-gray-600 mb-3">
                Download a copy of all your data including profile, applications, and messages.
              </p>
              <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                Request Data Export
              </button>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Account Deletion</h4>
              <p className="text-sm text-gray-600 mb-3">
                Permanently delete your account and all associated data. This action cannot be undone.
              </p>
              <button className="text-red-600 hover:text-red-700 text-sm font-medium">
                Delete Account
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end pt-6 border-t border-gray-200 mt-8">
        <button
          onClick={handleSave}
          disabled={loading}
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
        >
          {loading && <div className="mr-2"><LoadingSpinner size="sm" /></div>}
          {loading ? 'Saving...' : 'Save Preferences'}
        </button>
      </div>
    </div>
  );
}